import React, { useEffect, useState, useRef } from "react";
import { getTimechartData } from "../../services/summaryReportsService";
import { LineChart } from "../charts/LineChart";
import { BarColumnChart } from "../charts/BarColumnChart";
import { LoadingSpinner } from "../common/LoadingSpinner";
import { ChartContainerMessage } from "./ChartContainerMessage";
import {
	getDateFromEpoch,
	getMonthAbbreviation,
} from "../../services/dateService";

export const TimeChartContainer = ({
	code,
	handleShowFullscreenButton,
	selector,
}) => {
	const [data, setData] = useState([]);
	const [def, setDef] = useState(null);

	const [page, setPage] = useState("All");
	const [dataSets, setDataSets] = useState([]);

	const [hasErrored, setHasErrored] = useState(false);

	const mounted = useRef(false);

	const getDataSetsFromPage = (page) => {
		if (data.length === 0 || def === null) {
			return [];
		}

		const formatData = def.format.data;

		let headings;

		if (page && page.toLowerCase() !== "all") {
			headings = formatData.filter((e) => {
				return e.selector.includes(page);
			});
		} else {
			headings = formatData;
		}

		const selectedSets = headings
			.map((e) => {
				const found = data.find(
					(w) => w.name.toLowerCase() === e.display.toLowerCase()
				);
				if (found) {
					found.def = e;
					return found;
				}

				return null;
			})
			.filter((e) => e !== null);

		return selectedSets;
	};

	useEffect(() => {
		mounted.current = true;

		handleShowFullscreenButton(true);
		getTimechartData(code, selector)
			.then((responseData) => {
				const cnv = (val) => {
					const dateFromEpoch = getDateFromEpoch(parseInt(val, 10));
					const date = dateFromEpoch.getDate().toString().padStart(2, "0");
					const month = getMonthAbbreviation(dateFromEpoch.getMonth());
					const year = dateFromEpoch.getFullYear().toString().slice(-2);

					return `${date} ${month} ${year}`;
				};

				if (mounted.current === true) {
					// do we need to do any local processing
					if (responseData.fullDataCategories) {
						const categories = [];
						for (const c of responseData.fullDataCategories) {
							categories.push(cnv(c));
						}

						responseData.def.format.axes[0].categories = categories;
					}

					if (responseData.fullData) {
						for (const p of responseData.fullData) {
							for (const d of p.data) {
								d.label = cnv(d.dueDate);
								delete d.dueDate;
							}
						}

						responseData.data = responseData.fullData;
					}

					setDef(responseData.def);
					setData(responseData.data);
				}
			})
			.catch((e) => {
				console.error(e);
				if (mounted.current === true) {
					setHasErrored(true);
				}
			});

		return () => {
			mounted.current = false;
			handleShowFullscreenButton(false);
		};
	}, []);

	useEffect(() => {
		setDataSets(getDataSetsFromPage(page));
	}, [page]);

	useEffect(() => {
		if (data.length > 0 && def !== null) {
			if (def.format.defaultSelector) {
				setPage(def.format.defaultSelector);
			} else if (def.format.selectors && def.format.selectors.length > 0) {
				setPage(def.format.selectors[0]);
			}

			setDataSets(getDataSetsFromPage(page));
		}
	}, [data, def]);

	if (hasErrored === true) {
		return (
			<ChartContainerMessage
				message={[
					"It has not been possible to display your chart.",
					"This is most likely because the connection to the internet has been lost.",
					"If this problem persists, please contact our helpdesk, quoting reference '#TMC001'.",
				]}
			/>
		);
	}

	if (def === null) {
		return <LoadingSpinner />;
	}

	if (data.length === 0) {
		let text = "You need at least one completed report to display this chart.";
		if (def.subType === "line") {
			text = "You need at least two completed reports to display this chart.";
		}
		return <ChartContainerMessage message={text} />;
	}

	const pages = def.format.selectors ?? [];

	const getChart = () => {
		switch (def.subType.toLowerCase()) {
			case "bar":
			case "column":
				return (
					<BarColumnChart
						dataSets={dataSets}
						def={def}
						type={def.subType}
					></BarColumnChart>
				);
			case "line":
			default:
				return <LineChart dataSets={dataSets} def={def} />;
		}
	};

	return (
		<div className={"h-full flex flex-col"}>
			{pages.length > 1 ? (
				<ul className="flex flex-row mx-2 xs:mx-3 btnSlider mb-4 mt-2 relative overflow-auto">
					{pages.map((e, index) => {
						return (
							<li
								key={`timeChart${index}`}
								className={"flex-grow relative pr-1"}
							>
								<button
									key={`timeChartHeading${index}`}
									onClick={() => setPage(e)}
									className={`min-w-28 rounded-full text-sm ${
										page === e
											? "text-white bg-black font-bold"
											: "text-black bg-gray-100"
									}`}
								>
									{e}
								</button>
							</li>
						);
					})}
				</ul>
			) : null}
			<div className={"flex-1"}>{getChart()}</div>
		</div>
	);
};
